import * as React from 'react';
import * as acl from 'common/utils/acl';
import * as qs from 'qs';

import * as dateAddDays from 'date-fns/add_days';
import * as dateFormat from 'date-fns/format';

import { EntityActions, EntityType } from 'common/entity';
import { FilterOption, Filters } from 'common/components/Filters';
import { Route, RouteComponentProps } from 'react-router-dom';
import { getFilterTerms, stripQueryTerms } from '../../common/utils/search';

import { AppState } from 'common/models/AppState';
import { Filter } from 'common/components/Filters';
import { Page } from 'common/components/layout/Page';
import { Search } from 'common/components/Search';
import { UserPermissions } from 'common/api/models/UserPermissions';
import { UsersDetail } from './UsersDetail';
import { UsersDialog } from './UsersDialog';
import { UsersTable } from './UsersTable';
import { getExportUrl } from 'common/utils/api';
import { Member } from 'common/api/models/Member';
import { MemberApi } from 'common/api/resources/MemberApi';
import { Tag } from 'common/api/models/Tag';
import { Feature } from 'common/features/Feature';
import { canViewBadges } from 'common/features/canViewBadges';
import { ACCOUNT_MANAGER_FILTER_OPTIONS } from 'common/utils/memberConstants';

export interface UsersProps {
  appState: AppState;
  entityActions: EntityActions;
}
export interface UsersState {
  error?: string;
  editUser?: Member;
  isReady: boolean;
  savedAt: Date;
}

export const NEW_USER: Member = {
  vars: {
    certification: 'NONE',
    educationHours: 0,
  },
  chapterRole: Member.ChapterRole.NONE,
  status: Member.Status.ACTIVE,
  adminAccess: Member.AdminAccess.NONE,
};

export class Users extends React.Component<UsersProps & RouteComponentProps<{}>, UsersState> {
  state: UsersState = {
    isReady: false,
    savedAt: new Date(),
  };

  filters: Filter[] = [];
  archivedFilters: Filter[] = [];
  filterNames = [
    'status',
    'access',
    'member',
    'renewType',
    'cert',
    'tag',
    'expire',
    'wikiExpire',
    // 'certificationExpire',
    'chapter',
    'role',
    'contactType',
    'tag-internal',
    'accountManager',
  ];

  componentDidMount() {
    this.props.entityActions.fetchAll(EntityType.TAG, () => {
      this.props.entityActions.fetchAll(EntityType.CHAPTER, () => {
        let chapterOptions: FilterOption[] = [];
        for (let k in this.props.appState.entities.chapter) {
          if (this.props.appState.entities.chapter.hasOwnProperty(k)) {
            const chapter = this.props.appState.entities.chapter[k];
            chapterOptions.push({ label: chapter.name!, value: chapter.id! });
          }
        }
        chapterOptions.sort((a, b) => (a.label > b.label ? 1 : -1));
        chapterOptions.unshift({ label: 'All', value: '' });
        let accessOptions: FilterOption[] = [{ label: 'All', value: '' }];
        const accesses = acl.getAdminAccesses();
        for (let k in accesses) {
          if (accesses.hasOwnProperty(k)) {
            accessOptions.push({ label: (accesses as any)[k], value: k.toLowerCase() });
          }
        }
        let badgeOptions: FilterOption[] = [
          { label: 'All', value: '' },
          { label: 'None', value: 'none' },
          { label: 'Any', value: 'any' },
        ];
        for (let k in this.props.appState.entities.tag) {
          if (this.props.appState.entities.tag.hasOwnProperty(k)) {
            const tag = this.props.appState.entities.tag[k];
            if (tag.type === Tag.Type.BADGE) {
              badgeOptions.push({ label: tag.name!, value: tag.slug! });
            }
          }
        }
        let internalTagOptions: FilterOption[] = [{ label: 'All', value: '' }];
        for (let k in this.props.appState.entities.tag) {
          if (this.props.appState.entities.tag.hasOwnProperty(k)) {
            const tag = this.props.appState.entities.tag[k];
            if (tag.type === Tag.Type.INTERNAL) {
              internalTagOptions.push({ label: tag.name!, value: tag.slug! });
            }
          }
        }

        this.filters = [
          {
            name: 'status',
            label: 'Status',
            options: [
              { label: 'All', value: '' },
              { label: 'Inactive', value: 'inactive' },
              { label: 'Expired', value: 'expired' },
              { label: 'Active', value: 'active' },
            ],
          },
          {
            name: 'access',
            label: 'Access',
            options: accessOptions,
          },
          {
            name: 'member',
            label: 'Member Type',
            options: [
              { label: 'All', value: '' },
              { label: 'EM', value: 'EM' },
              { label: 'IM', value: 'IM' },
              { label: 'STUDENT', value: 'STUDENT' },
              { label: 'HON', value: 'HON' },
              { label: 'RG', value: 'RG' },
              { label: 'RETIRED', value: 'RETIRED' },
            ],
          },
          {
            name: 'renewType',
            label: 'Renewal Type',
            options: [
              { label: 'All', value: '' },
              { label: 'Invoice', value: 'BILL' },
              { label: 'Auto-Renew', value: 'AUTO' },
              { label: 'Manual', value: 'MANUAL' },
            ],
          },
          {
            name: 'cert',
            label: 'Certification',
            options: [
              { label: 'All', value: '' },
              { label: 'None', value: 'none' },
              { label: 'Any', value: '*' },
              { label: 'AKBD', value: 'akbd' },
              { label: 'CBD', value: 'cbd' },
              { label: 'CKD', value: 'ckd' },
              { label: 'CKD + CBD', value: 'ckd-cbd' },
              { label: 'CKBD', value: 'ckbd' },
              { label: 'CKBI', value: 'ckbi' },
              { label: 'CMKBD', value: 'cmkbd' },
            ],
          },
          /* {
            name: 'certificationExpire',
            label: 'Certification Expires',
            options: [
              { label: 'All', value: '' },
              {
                label: 'Past 120 Days',
                value:
                  dateFormat(dateAddDays(new Date(), -120), 'YYYY-MM-DD') + ',' + dateFormat(new Date(), 'YYYY-MM-DD'),
              },
              {
                label: 'Past 90 Days',
                value:
                  dateFormat(dateAddDays(new Date(), -90), 'YYYY-MM-DD') + ',' + dateFormat(new Date(), 'YYYY-MM-DD'),
              },
              {
                label: 'Past 60 Days',
                value:
                  dateFormat(dateAddDays(new Date(), -60), 'YYYY-MM-DD') + ',' + dateFormat(new Date(), 'YYYY-MM-DD'),
              },
              {
                label: 'Past 30 Days',
                value:
                  dateFormat(dateAddDays(new Date(), -30), 'YYYY-MM-DD') + ',' + dateFormat(new Date(), 'YYYY-MM-DD'),
              },
              {
                label: 'Past 7 Days',
                value:
                  dateFormat(dateAddDays(new Date(), -7), 'YYYY-MM-DD') + ',' + dateFormat(new Date(), 'YYYY-MM-DD'),
              },
              {
                label: 'Next 7 Days',
                value:
                  dateFormat(new Date(), 'YYYY-MM-DD') + ',' + dateFormat(dateAddDays(new Date(), 7), 'YYYY-MM-DD'),
              },
              {
                label: 'Next 30 Days',
                value:
                  dateFormat(new Date(), 'YYYY-MM-DD') + ',' + dateFormat(dateAddDays(new Date(), 30), 'YYYY-MM-DD'),
              },
              {
                label: 'Next 60 Days',
                value:
                  dateFormat(new Date(), 'YYYY-MM-DD') + ',' + dateFormat(dateAddDays(new Date(), 60), 'YYYY-MM-DD'),
              },
              {
                label: 'Next 90 Days',
                value:
                  dateFormat(new Date(), 'YYYY-MM-DD') + ',' + dateFormat(dateAddDays(new Date(), 90), 'YYYY-MM-DD'),
              },
              {
                label: 'Next 120 Days',
                value:
                  dateFormat(new Date(), 'YYYY-MM-DD') + ',' + dateFormat(dateAddDays(new Date(), 120), 'YYYY-MM-DD'),
              },
            ],
          },
          */
          ...(canViewBadges(Feature.badges, this.props.appState.config, this.props.appState.user)
            ? [
                {
                  name: 'tag',
                  label: 'Badge',
                  options: badgeOptions,
                },
              ]
            : []),
          {
            name: 'tag-internal',
            label: 'Internal Tags',
            options: internalTagOptions,
          },
          {
            name: 'expire',
            label: 'Expires',
            options: [
              { label: 'All', value: '' },
              {
                label: 'Past 120 Days',
                value:
                  dateFormat(dateAddDays(new Date(), -120), 'YYYY-MM-DD') + ',' + dateFormat(new Date(), 'YYYY-MM-DD'),
              },
              {
                label: 'Past 90 Days',
                value:
                  dateFormat(dateAddDays(new Date(), -90), 'YYYY-MM-DD') + ',' + dateFormat(new Date(), 'YYYY-MM-DD'),
              },
              {
                label: 'Past 60 Days',
                value:
                  dateFormat(dateAddDays(new Date(), -60), 'YYYY-MM-DD') + ',' + dateFormat(new Date(), 'YYYY-MM-DD'),
              },
              {
                label: 'Past 30 Days',
                value:
                  dateFormat(dateAddDays(new Date(), -30), 'YYYY-MM-DD') + ',' + dateFormat(new Date(), 'YYYY-MM-DD'),
              },
              {
                label: 'Past 7 Days',
                value:
                  dateFormat(dateAddDays(new Date(), -7), 'YYYY-MM-DD') + ',' + dateFormat(new Date(), 'YYYY-MM-DD'),
              },
              {
                label: 'Next 7 Days',
                value:
                  dateFormat(new Date(), 'YYYY-MM-DD') + ',' + dateFormat(dateAddDays(new Date(), 7), 'YYYY-MM-DD'),
              },
              {
                label: 'Next 30 Days',
                value:
                  dateFormat(new Date(), 'YYYY-MM-DD') + ',' + dateFormat(dateAddDays(new Date(), 30), 'YYYY-MM-DD'),
              },
              {
                label: 'Next 60 Days',
                value:
                  dateFormat(new Date(), 'YYYY-MM-DD') + ',' + dateFormat(dateAddDays(new Date(), 60), 'YYYY-MM-DD'),
              },
              {
                label: 'Next 90 Days',
                value:
                  dateFormat(new Date(), 'YYYY-MM-DD') + ',' + dateFormat(dateAddDays(new Date(), 90), 'YYYY-MM-DD'),
              },
              {
                label: 'Next 120 Days',
                value:
                  dateFormat(new Date(), 'YYYY-MM-DD') + ',' + dateFormat(dateAddDays(new Date(), 120), 'YYYY-MM-DD'),
              },
            ],
          },
          {
            name: 'wikiExpire',
            label: 'Wiki Expires',
            options: [
              { label: 'All', value: '' },
              {
                label: 'Past 120 Days',
                value:
                  dateFormat(dateAddDays(new Date(), -120), 'YYYY-MM-DD') + ',' + dateFormat(new Date(), 'YYYY-MM-DD'),
              },
              {
                label: 'Past 90 Days',
                value:
                  dateFormat(dateAddDays(new Date(), -90), 'YYYY-MM-DD') + ',' + dateFormat(new Date(), 'YYYY-MM-DD'),
              },
              {
                label: 'Past 60 Days',
                value:
                  dateFormat(dateAddDays(new Date(), -60), 'YYYY-MM-DD') + ',' + dateFormat(new Date(), 'YYYY-MM-DD'),
              },
              {
                label: 'Past 30 Days',
                value:
                  dateFormat(dateAddDays(new Date(), -30), 'YYYY-MM-DD') + ',' + dateFormat(new Date(), 'YYYY-MM-DD'),
              },
              {
                label: 'Past 7 Days',
                value:
                  dateFormat(dateAddDays(new Date(), -7), 'YYYY-MM-DD') + ',' + dateFormat(new Date(), 'YYYY-MM-DD'),
              },
              {
                label: 'Next 7 Days',
                value:
                  dateFormat(new Date(), 'YYYY-MM-DD') + ',' + dateFormat(dateAddDays(new Date(), 7), 'YYYY-MM-DD'),
              },
              {
                label: 'Next 30 Days',
                value:
                  dateFormat(new Date(), 'YYYY-MM-DD') + ',' + dateFormat(dateAddDays(new Date(), 30), 'YYYY-MM-DD'),
              },
              {
                label: 'Next 60 Days',
                value:
                  dateFormat(new Date(), 'YYYY-MM-DD') + ',' + dateFormat(dateAddDays(new Date(), 60), 'YYYY-MM-DD'),
              },
              {
                label: 'Next 90 Days',
                value:
                  dateFormat(new Date(), 'YYYY-MM-DD') + ',' + dateFormat(dateAddDays(new Date(), 90), 'YYYY-MM-DD'),
              },
              {
                label: 'Next 120 Days',
                value:
                  dateFormat(new Date(), 'YYYY-MM-DD') + ',' + dateFormat(dateAddDays(new Date(), 120), 'YYYY-MM-DD'),
              },
            ],
          },
          { name: 'chapter', label: 'Chapter', options: chapterOptions },
          {
            name: 'role',
            label: 'Role',
            options: [
              { label: 'All', value: '' },
              { label: 'None', value: 'none' },
              { label: 'Member', value: 'member' },
              { label: 'Officer', value: 'officer' },
            ],
          },
          {
            name: 'contactType',
            label: 'Contact Type',
            options: [
              { label: 'All', value: '' },
              { value: 'PRIMARY', label: 'Primary' },
              { value: 'KBIS', label: 'KBIS' },
              { value: 'MARKETING', label: 'Marketing' },
              { value: 'HR', label: 'HR' },
              { value: 'TRAINING', label: 'Training' },
              { value: 'PRESIDENT', label: 'President' },
              { value: 'CEO', label: 'CEO' },
            ],
          },
          {
            name: 'accountManager',
            label: 'Account Manager',
            options: ACCOUNT_MANAGER_FILTER_OPTIONS,
          },
        ];
        this.archivedFilters = this.filters.filter(
          filter => filter.name !== 'status' && filter.name !== 'expire' && filter.name !== 'wikiExpire'
        );
        this.setState({ isReady: true });
      });
    });
  }

  componentDidUpdate(prevProps: UsersProps & RouteComponentProps<{}>) {
    const prevState = prevProps.location.state as { successMessage?: string } | undefined;
    const currentState = this.props.location.state as { successMessage?: string } | undefined;
    if (
      currentState &&
      currentState.successMessage &&
      (!prevState || prevState.successMessage !== currentState.successMessage)
    ) {
      this.props.history.replace(this.props.location.pathname + this.props.location.search, {});
    }
  }

  isArchivedMode = (query: string) => query.includes('archived:1');

  handleExportArchivedCsv = () => {
    const query = this.getQuery();
    MemberApi.exportArchivedCsv(
      {
        q: query + ' type:user',
      },
      {
        responseType: 'blob',
      }
    ).then(
      data => {
        const csvBlob = data instanceof Blob ? data : new Blob([data as any], { type: 'text/csv;charset=utf-8;' });
        const downloadUrl = window.URL.createObjectURL(csvBlob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', 'archived-users.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      },
      err => {
        this.setState({ error: err && err.message ? err.message : 'Failed to export archived users CSV.' });
      }
    );
  };

  handleQuery = (query: string) => {
    if (!query) {
      this.props.history.push(this.props.match.url);
    } else {
      this.props.history.push(this.props.match.url + '?' + qs.stringify({ q: query }));
    }
  };

  getQuery() {
    const q = qs.parse(this.props.location.search.substring(1)).q;
    return q !== undefined ? q : '';
  }

  handleSubmitSearch = (value: string) => {
    const query = this.getQuery();
    const filterValue = getFilterTerms(query, this.filterNames);
    const newSearchQuery = (value + ' ' + filterValue).trim();
    this.handleQuery(newSearchQuery);
  };

  handleAdd = () => {
    this.setState({ editUser: NEW_USER });
  };

  handleEdit = (u: Member) => {
    this.setState({ editUser: u });
  };

  handleClickRow = (num: string) => {
    this.props.history.push('/users/' + num, { fromArchivedMode: this.isArchivedMode(this.getQuery()) });
  };

  handleCancelDialog = () => {
    this.setState({ editUser: undefined });
  };

  handleSaveDialog = (id: string) => {
    this.props.entityActions.refetch(EntityType.MEMBER, id);
    this.setState({ editUser: undefined, savedAt: new Date() });
  };

  render() {
    if (!this.state.isReady) {
      return null;
    }

    const query = this.getQuery();
    const archivedMode = this.isArchivedMode(query);
    const searchQuery = stripQueryTerms(query, this.filterNames);
    const exportCsvUrl = getExportUrl('/v2/member/export', {
      q: query + ' type:user',
    });
    const locationState = this.props.location.state as { successMessage?: string } | undefined;
    const resetPath = archivedMode ? '/users?q=archived:1' : '/users?q=status:active';
    return (
      <div>
        {locationState &&
          locationState.successMessage && (
            <div className="alert alert-success" style={{ marginTop: 20 }}>
              {locationState.successMessage}
            </div>
          )}
        {this.state.error && (
          <div className="alert alert-danger" style={{ marginTop: 20 }}>
            {this.state.error}
          </div>
        )}

        <Route
          path="/users"
          exact={true}
          render={(props: RouteComponentProps<{}>) => (
            <Page
              title={archivedMode ? 'Archived Users' : 'Users'}
              primaryAction={{
                shouldRender:
                  acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_MEMBER) &&
                  this.props.appState.user &&
                  this.props.appState.user.adminAccess !== Member.AdminAccess.CHAPTERSADMIN,
                onClick: this.handleAdd,
                label: 'Add User',
              }}
              additionalPrimaryActions={[
                {
                  shouldRender:
                    !!this.props.appState.user &&
                    (this.props.appState.user.adminAccess === Member.AdminAccess.ADMIN ||
                      this.props.appState.user.adminAccess === Member.AdminAccess.SUPERUSER),
                  to: archivedMode ? '/users?q=status:active' : '/users?q=archived:1',
                  iconName: archivedMode ? 'users' : 'archive',
                  label: archivedMode ? 'Active Users' : 'Archived Users',
                },
              ]}
              secondaryActions={[
                {
                  shouldRender: acl.hasPermission(this.props.appState, UserPermissions.Permissions.EXPORT),
                  ...(archivedMode ? { onClick: this.handleExportArchivedCsv } : { href: exportCsvUrl }),
                  iconName: 'download',
                  label: 'Export CSV',
                },
              ]}
            >
              <div className="card card-responsive">
                <div className="p-3 p-sm-4">
                  <div className="mb-3">
                    <Search
                      placeholder="Search by name or email"
                      onSubmit={this.handleSubmitSearch}
                      initialValue={searchQuery}
                      pathToClearSearch={resetPath}
                    />
                  </div>
                  <div className="mb-3">
                    <Filters
                      value={query}
                      filters={archivedMode ? this.archivedFilters : this.filters}
                      onChange={this.handleQuery}
                    />
                    <UsersTable
                      appState={this.props.appState}
                      entityActions={this.props.entityActions}
                      search={query}
                      archivedMode={archivedMode}
                      onClick={this.handleClickRow}
                      onEdit={this.handleEdit}
                    />
                  </div>
                </div>
              </div>
            </Page>
          )}
        />
        <Route
          path="/users/:number"
          render={(props: RouteComponentProps<{ id: string }>) => (
            <UsersDetail
              appState={this.props.appState}
              entityActions={this.props.entityActions}
              number={(props.match.params as any).number}
              onEdit={this.handleEdit}
              reloadAt={this.state.savedAt}
              {...props}
            />
          )}
        />

        <UsersDialog
          user={this.state.editUser}
          appState={this.props.appState}
          onSave={this.handleSaveDialog}
          onCancel={this.handleCancelDialog}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/Users/Users.tsx